import { Pressable, View } from "react-native";
import { spacing, useTheme } from "~/styles/theme";
import { CaptionText, FigureText, LabelText } from "~/components/ui/Text";
import { Icon } from "~/components/ui/Icon";
import {
  computeCounterValue,
  describeCounterValue,
  formatDayCount,
} from "~/utils/dates";
import type { Counter } from "~/types";

export function CounterCard({
  counter,
  onPress,
}: {
  counter: Counter;
  onPress: () => void;
}) {
  const { colors } = useTheme();
  const value = computeCounterValue(counter);
  const caption = describeCounterValue(counter.direction, value);
  const figureColor = counter.color === "red" ? colors.accent : colors.ink;

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`${counter.label || "Untitled"}, ${caption}`}
      onPress={onPress}
      style={({ pressed }) => [
        {
          flexBasis: "50%",
          flexGrow: 1,
          // No card box — this cell is one square in a shared grid; the
          // right/bottom lines it draws plus the grid's own top/left border
          // (see index.tsx) are the only rules, never doubled up between
          // neighbours.
          borderRightWidth: 1,
          borderBottomWidth: 1,
          borderColor: colors.rule,
          padding: spacing.md,
          minHeight: 148,
          justifyContent: "space-between",
          opacity: pressed ? 0.6 : 1,
        },
      ]}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "flex-start",
          justifyContent: "space-between",
        }}
      >
        <LabelText
          numberOfLines={2}
          style={{ flex: 1, marginRight: spacing.xs }}
          color={colors.muted}
        >
          {counter.label || "Untitled"}
        </LabelText>
        <Icon
          name={counter.direction === "up" ? "arrow-up" : "arrow-down"}
          size="small"
          tone="muted"
        />
      </View>

      <View>
        <FigureText
          color={figureColor}
          style={{ fontSize: 40, lineHeight: 42 }}
          numberOfLines={1}
          adjustsFontSizeToFit
        >
          {formatDayCount(value.days)}
        </FigureText>
        <CaptionText
          color={value.isPending ? colors.accent : colors.muted}
          style={{
            textTransform: "uppercase",
            letterSpacing: 0.6,
            marginTop: 2,
          }}
        >
          {caption}
        </CaptionText>
      </View>
    </Pressable>
  );
}
